import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useContext, useMemo } from 'react';
import { LegacyDevice } from 'console/device/device';
import { usePartialAppSelector } from 'shared/hooks/redux/hooks';
import { getLegacyHeatingCircuitsConfig } from '../../content/device/heating-circuits/legacy';
import { getLegacyHeatingModes } from '../../content/device/heating-modes/legacy';
import { DeviceUISettingsProvider } from '../device-ui-settings';
import { DeviceConfig } from '../device/config/types';
import { legacy_boiler_heating_circuits_config, legacy_heating_circuits_config, } from '../../content/device/heating-circuits/legacy/utils';
import { legacy_heating_modes_config } from '../../content/device/heating-modes/legacy/utils';
import { Capability } from 'console/device/device-capability';
import { getTabConfig } from '../utils';
import { createSelector } from '@reduxjs/toolkit';
import { createSubsetSelector } from 'webui/utils/selectors';
import { getLegacyBoilerHeatingCircuits } from '../../content/device/heating-circuits/legacy/boiler';
import { legacy_guard_controls_config } from '../../content/device/guard-control/legacy/utils';
import { getLegacyGuardControlsConfig } from '../../content/device/guard-control/legacy';
import { getLegacyThermometers } from '../../content/device/thermometers/legacy';
import { legacy_thermometers_config } from '../../content/device/thermometers/legacy/utils';
import { getLegacySensors } from '../../content/device/sensors/legacy';
import { legacy_sensors_config } from '../../content/device/sensors/legacy/utils';
import { legacy_custom_controls_config } from '../../content/device/custom-controls/legacy/utils';
import { getLegacyCustomControls } from '../../content/device/custom-controls/legacy';
import { selectDevice } from 'entities/device/lib';
import { HeatingDeviceEntitiesConfig } from './types';
const select_default_device = createSelector(selectDevice, createSubsetSelector([
    'capabilities',
    'thermostat_enabled',
    'firmware_version',
    'device_type',
]));
const select_thermostat_target_config = (device) => {
    const thermostat_target_temps = Object.fromEntries(Object.entries(device?.thermostat_target_temps || {}).map(([key, target_temp]) => {
        const { temp, manual, ...config } = target_temp;
        return [key, config];
    }));
    return {
        ...device,
        thermostat_target_temps,
    };
};
const select_heating_circuits_device = createSelector(selectDevice, legacy_heating_circuits_config);
const select_heating_modes_device = createSelector(selectDevice, legacy_heating_modes_config);
const select_boiler_heating_circuits_device = createSelector(selectDevice, legacy_boiler_heating_circuits_config);
const select_guard_controls_device = createSelector(selectDevice, legacy_guard_controls_config);
const select_thermometers_device = createSelector(selectDevice, legacy_thermometers_config);
const select_sensors_device = createSelector(selectDevice, legacy_sensors_config);
const select_custom_controls_device = createSelector(selectDevice, legacy_custom_controls_config);
export const LegacyHeatingDeviceConfigProvider = memo(function LegacyHeatingCircuitContentProvider(props) {
    const { children, templates, support_ui_settings } = props;
    const { has_guard_control, device_id } = useContext(DeviceConfig);
    const device = usePartialAppSelector(select_default_device, device_id);
    const support_heating_control = useMemo(() => {
        if (!device) {
            return false;
        }
        const has_thermostat = LegacyDevice.has_capability(device?.capabilities, Capability.has_thermostat);
        const has_optional_thermostat = LegacyDevice.has_capability(device?.capabilities, Capability.has_optional_thermostat);
        return ((LegacyDevice.supports_thermostat_ext_modes({
            capabilities: device.capabilities,
            device_type: device.device_type,
            thermostat_enabled: device.thermostat_enabled,
            firmware_version: device.firmware_version,
        }) &&
            has_thermostat) ||
            (has_optional_thermostat && device.thermostat_enabled));
    }, [
        device?.capabilities,
        device?.thermostat_enabled,
        device?.firmware_version,
        device?.device_type,
    ]);
    const heating_circuits_device = support_heating_control
        ? usePartialAppSelector(select_heating_circuits_device, device_id)
        : null;
    const heating_circuits = useMemo(() => heating_circuits_device &&
        getLegacyHeatingCircuitsConfig(heating_circuits_device), [heating_circuits_device]);
    const heating_modes_device = support_heating_control
        ? usePartialAppSelector(select_heating_modes_device, device_id)
        : null;
    const heating_modes = useMemo(() => heating_modes_device && getLegacyHeatingModes(heating_modes_device), [heating_modes_device]);
    const boiler_heating_circuits_device = usePartialAppSelector(select_boiler_heating_circuits_device, device_id) ??
        null;
    const boiler_heating_circuits = useMemo(() => {
        if (!boiler_heating_circuits_device) {
            return null;
        }
        if (!support_heating_control) {
            return null;
        }
        return getLegacyBoilerHeatingCircuits(boiler_heating_circuits_device);
    }, [boiler_heating_circuits_device, support_heating_control]);
    const guard_controls_device = has_guard_control
        ? usePartialAppSelector(select_guard_controls_device, device_id)
        : null;
    const guard_controls = useMemo(() => guard_controls_device
        ? getLegacyGuardControlsConfig(guard_controls_device)
        : null, [guard_controls_device]);
    const thermometers_device = usePartialAppSelector(select_thermometers_device, device_id);
    const thermometers = useMemo(() => thermometers_device && getLegacyThermometers(thermometers_device), [thermometers_device]);
    const sensors_device = usePartialAppSelector(select_sensors_device, device_id);
    const sensors = useMemo(() => sensors_device && getLegacySensors(sensors_device), [sensors_device]);
    const custom_controls_device = usePartialAppSelector(select_custom_controls_device, device_id);
    const custom_controls = useMemo(() => custom_controls_device &&
        getLegacyCustomControls(custom_controls_device), [custom_controls_device]);
    const device_tab_config = useMemo(() => getTabConfig({
        heating_circuits,
        boiler_heating_circuits,
        heating_modes,
        thermometers,
        guard_controls,
        sensors,
        custom_controls,
    }), [
        heating_circuits,
        boiler_heating_circuits,
        heating_modes,
        thermometers,
        guard_controls,
        sensors,
        custom_controls,
    ]);
    const provider = useMemo(() => {
        if (support_ui_settings) {
            return (_jsx(DeviceUISettingsProvider, { device_config: device_tab_config, templates: templates, children: children }));
        }
        return children;
    }, [support_ui_settings, children, device_tab_config]);
    return (_jsx(HeatingDeviceEntitiesConfig.Provider, { value: device_tab_config, children: provider }));
});
